import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../utils/config";
import CustomSelect from "../components/CustomSelect";
import { TeamTag } from "../components/TeamTag";

/**
 * "Barang di Luar" — every approved barang keluar that hasn't fully come back.
 *
 * Closing a row happens here rather than through the generic transaction form
 * on purpose: returning barang starts from looking at what is still out, and
 * picking the exact row you're settling is what makes the rekap clearable at
 * all. The form can't offer that — it starts from an item, not from a debt.
 */

const STATUS_OPTIONS = [
  { value: "",        label: "Semua" },
  { value: "OVERDUE", label: "Lewat jatuh tempo" },
];

function dueBadge(row) {
  if (row.is_overdue) {
    return <span className="badge badge-danger">Telat {Math.abs(row.days_until_due)} hari</span>;
  }
  if (row.days_until_due === null || row.days_until_due === undefined) {
    return <span className="badge badge-info">Tanpa tenggat</span>;
  }
  if (row.days_until_due === 0) return <span className="badge badge-danger">Hari ini</span>;
  if (row.days_until_due === 1) return <span className="badge badge-warning">Besok</span>;
  return <span className="badge badge-info">{row.days_until_due} hari lagi</span>;
}

export default function OutgoingItems() {
  const [rows, setRows]         = useState([]);
  const [teams, setTeams]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [notice, setNotice]     = useState("");
  const [actingId, setActingId] = useState(null);
  const [shareUrl, setShareUrl] = useState("");

  // Inline settle panel — replaces window.prompt (hasil meet.txt's mobile-ACC
  // bug was specifically about the pending-queue popup, but this page uses
  // the exact same unreliable pattern for the same "barang tidak kembali"
  // decision, so it gets the same fix for consistency). { rowId, type } | null
  const [settlePanel, setSettlePanel] = useState(null);
  const [settleQty, setSettleQty] = useState("");
  const [settleNote, setSettleNote] = useState("");
  const [settleError, setSettleError] = useState("");

  const [status, setStatus] = useState("");
  const [team, setTeam]     = useState("");
  const [search, setSearch] = useState("");

  const token = localStorage.getItem("accessToken");
  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const readError = async (res, fallback) => {
    const d = await res.json().catch(() => null);
    if (!d) return fallback;
    if (typeof d === "string") return d;
    if (d.detail) return d.detail;
    const first = Object.values(d)[0];
    if (Array.isArray(first)) return String(first[0]);
    return typeof first === "string" ? first : fallback;
  };

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (team) p.set("team", team);
    if (search.trim()) p.set("search", search.trim());
    return p.toString();
  }, [status, team, search]);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/transactions/outstanding/?${query}`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRows(Array.isArray(d) ? d : d.results || []))
      .catch(() => setError("Gagal memuat daftar barang di luar."))
      .finally(() => setLoading(false));
  }, [authHeaders, query]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/teams/?module=inventory&active_only=1`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setTeams(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [authHeaders]);

  const flash = (msg) => {
    setNotice(msg);
    setError("");
    window.setTimeout(() => setNotice(""), 3500);
  };

  const teamOptions = useMemo(
    () => [
      { value: "", label: "Semua kelompok" },
      ...teams.map((t) => ({ value: String(t.id), label: t.name })),
    ],
    [teams]
  );

  // Both "Kembalikan" and "Terpakai" post an ordinary transaction naming the
  // row it settles, so stock and the outstanding counter move together — the
  // backend does both inside one atomic block and re-checks the remainder
  // under a row lock, so two people closing the same row can't overshoot it.
  const openSettlePanel = (row, type) => {
    setSettleQty(String(row.remaining));
    setSettleNote("");
    setSettleError("");
    setSettlePanel({ rowId: row.id, type });
  };

  const closeSettlePanel = () => {
    setSettlePanel(null);
    setSettleQty("");
    setSettleNote("");
    setSettleError("");
  };

  const confirmSettlePanel = () => {
    if (!settlePanel) return;
    const row = rows.find((r) => r.id === settlePanel.rowId);
    if (!row) { closeSettlePanel(); return; }
    const { type } = settlePanel;

    const qty = Number(settleQty);
    if (!Number.isInteger(qty) || qty <= 0) {
      setSettleError("Jumlah harus angka bulat lebih dari 0.");
      return;
    }
    if (qty > row.remaining) {
      setSettleError(`Maksimal ${row.remaining} — itu yang belum kembali.`);
      return;
    }
    // Admin inventory: barang terpakai permanen wajib kasih note, untuk
    // memudahkan tracking barang. "Kembalikan" doesn't need one — the item
    // is back, there's nothing to explain.
    if (type === "TEMP_OUT" && !settleNote.trim()) {
      setSettleError("Catatan wajib diisi untuk barang yang ditandai terpakai.");
      return;
    }

    const who = row.assign_name || row.assign_username || row.requested_by || "-";
    const label = type === "RETURN" ? "RETURN" : "TERPAKAI";
    let note = `${label} | ${row.item_code} — ${row.item_name} | ${row.location_name} | Atas Nama: ${who}`;
    if (type === "TEMP_OUT" && settleNote.trim()) {
      note += ` | ${settleNote.trim()}`;
    }

    setActingId(row.id);
    setError("");
    fetch(`${API_BASE_URL}/api/transactions/create/`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({
        item_id: row.item_id,
        location_id: row.location_id,
        transaction_type: type,
        quantity: qty,
        settles_id: row.id,
        note,
      }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readError(res, "Gagal memproses."));
        return res.json();
      })
      .then(() => {
        flash(
          type === "RETURN"
            ? `${qty} ${row.item_code} dikembalikan.`
            : `${qty} ${row.item_code} ditandai terpakai.`
        );
        closeSettlePanel();
        load();
      })
      .catch((e) => setSettleError(e.message))
      .finally(() => setActingId(null));
  };

  // Mints a token signing the *current* filters — factored out so both the
  // manual "Bagikan rekap" click and the filter-change auto-refresh below
  // mint from the same place instead of duplicating the fetch chain.
  const requestShareUrl = () =>
    fetch(`${API_BASE_URL}/api/transactions/share-link/?${query}`, { headers: authHeaders })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readError(res, "Gagal membuat link."));
        return res.json();
      })
      .then((d) => `${window.location.origin}/rekap/barang-keluar?token=${encodeURIComponent(d.token)}`);

  const makeShareLink = () => {
    setError("");
    requestShareUrl()
      .then((url) => {
        setShareUrl(url);
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(
            () => flash("Link disalin ke clipboard."),
            () => flash("Link dibuat.")
          );
        } else {
          flash("Link dibuat.");
        }
      })
      .catch((e) => setError(e.message));
  };

  const copyShareUrl = () => {
    if (!shareUrl) return;
    if (!navigator.clipboard) { flash("Clipboard tidak tersedia di browser ini."); return; }
    navigator.clipboard.writeText(shareUrl).then(
      () => flash("Link disalin ke clipboard."),
      () => {}
    );
  };

  // A minted token bakes in status/team at that moment, so leaving a stale
  // link on screen after switching kelompok is exactly what sends a "nagih"
  // link for the wrong kelompok. Once a link has been shared, re-mint it
  // whenever the dropdown filters change — but not on "Cari" keystrokes,
  // which would otherwise mint (and audit-log) a fresh token per character.
  useEffect(() => {
    if (!shareUrl) return;
    setError("");
    requestShareUrl()
      .then((url) => { setShareUrl(url); flash("Link diperbarui sesuai filter."); })
      .catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, team]);

  const totalRemaining = rows.reduce((sum, r) => sum + (r.remaining || 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[28px] font-bold text-gradient leading-none">Barang di Luar</h1>
          <p className="text-[13px] mt-1" style={{ color: "var(--text-secondary)" }}>
            Barang yang sudah keluar gudang dan belum kembali. Tutup barisnya waktu
            barang balik, atau tandai terpakai kalau memang tidak kembali.
          </p>
        </div>
        <button type="button" onClick={makeShareLink} className="btn-outline">
          Bagikan rekap
        </button>
      </div>

      {error && (
        <div className="rounded-xl px-4 py-3 text-[13px] border animate-fade-in"
          style={{ color: "var(--danger)", background: "var(--danger-subtle)", borderColor: "color-mix(in srgb, var(--danger) 30%, transparent)" }}>
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-xl px-4 py-3 text-[13px] border animate-fade-in"
          style={{ color: "var(--success)", background: "var(--success-subtle)", borderColor: "color-mix(in srgb, var(--success) 30%, transparent)" }}>
          {notice}
        </div>
      )}
      {shareUrl && (
        <div className="rounded-xl px-4 py-3 text-[12px] border break-all"
          style={{ color: "var(--text-secondary)", background: "var(--bg-surface-hover)", borderColor: "var(--border)" }}>
          <span className="font-semibold">Link publik (berlaku 30 hari, tanpa login) — klik untuk menyalin:</span>
          <button
            type="button"
            onClick={copyShareUrl}
            title="Klik untuk salin ke clipboard"
            className="mt-1 font-mono break-all block w-full text-left bg-transparent border-0 p-0 cursor-pointer hover:underline"
            style={{ color: "var(--accent)" }}
          >
            {shareUrl}
          </button>
        </div>
      )}

      {/* filters — same three the share link signs, so what you see is what you send */}
      <div className="glass-panel p-4 grid gap-3 sm:grid-cols-3">
        <div>
          <label className="block text-[11px] font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
            Status
          </label>
          <CustomSelect value={status} onChange={setStatus} options={STATUS_OPTIONS} />
        </div>
        <div>
          <label className="block text-[11px] font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
            Kelompok
          </label>
          <CustomSelect value={team} onChange={setTeam} options={teamOptions} />
        </div>
        <div>
          <label className="block text-[11px] font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
            Cari
          </label>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            className="glass-input" placeholder="kode, nama barang, atas nama…" />
        </div>
      </div>

      {!loading && rows.length > 0 && (
        <div className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
          <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{rows.length}</span> baris ·{" "}
          <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{totalRemaining}</span> barang belum kembali
        </div>
      )}

      <div className="glass-table-wrap overflow-x-auto">
        <table className="glass-table">
          <thead>
            <tr>
              <th className="text-left">Barang</th>
              <th className="!text-right">Belum kembali</th>
              <th className="text-left">Atas Nama</th>
              <th className="text-left">Kelompok</th>
              <th className="text-left">Gudang</th>
              <th className="text-left cell-wrap">Catatan</th>
              <th className="text-left">Jatuh Tempo</th>
              <th className="!text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-5 py-8 text-center text-muted">Memuat…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-8 text-center text-muted">
                Tidak ada barang yang belum kembali.
              </td></tr>
            )}
            {!loading && rows.map((row) => {
              const panelOpen = settlePanel?.rowId === row.id;
              return (
              <React.Fragment key={row.id}>
              <tr>
                <td className="px-4 py-2">
                  <span className="font-mono text-brand-600 dark:text-brand-400 text-xs mr-1">
                    {row.item_code}
                  </span>
                  {row.item_name}
                </td>
                <td className="px-4 py-2 text-right font-semibold">
                  {row.remaining}
                  {row.remaining !== row.quantity && (
                    <span className="text-xs text-muted font-normal"> / {row.quantity}</span>
                  )}{" "}
                  <span className="text-xs text-muted font-normal">{row.item_unit}</span>
                </td>
                <td className="px-4 py-2">
                  {row.assign_name || row.assign_username || "-"}
                  {row.requested_by && (
                    <div className="text-[11px] text-muted">diajukan {row.requested_by}</div>
                  )}
                </td>
                <td className="px-4 py-2"><TeamTag name={row.requester_team_name} /></td>
                <td className="px-4 py-2 text-muted">{row.location_name}</td>
                <td className="px-4 py-2 text-muted cell-wrap">{row.note || "-"}</td>
                <td className="px-4 py-2">
                  <div className="text-xs">{row.due_date || "-"}</div>
                  <div className="mt-1">{dueBadge(row)}</div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      disabled={actingId === row.id}
                      onClick={() => openSettlePanel(row, "RETURN")}
                      className="rounded-md bg-green-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-green-700 disabled:opacity-60"
                    >
                      {actingId === row.id ? "…" : "Kembalikan"}
                    </button>
                    <button
                      type="button"
                      disabled={actingId === row.id}
                      onClick={() => openSettlePanel(row, "TEMP_OUT")}
                      className="rounded-md bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-amber-700 disabled:opacity-60"
                      title="Barang tidak kembali — tutup baris ini tanpa menambah stok"
                    >
                      Terpakai
                    </button>
                  </div>
                </td>
              </tr>
              {panelOpen && (
                <tr className="bg-black/5 dark:bg-white/5">
                  <td colSpan={8} className="px-4 py-3">
                    <div className="flex flex-wrap items-end justify-end gap-3">
                      <div>
                        <label className="block text-xs font-medium text-muted mb-1">
                          Jumlah {settlePanel.type === "RETURN" ? "dikembalikan" : "terpakai"} *
                        </label>
                        <input
                          type="number"
                          min="1"
                          max={row.remaining}
                          value={settleQty}
                          onChange={(e) => { setSettleQty(e.target.value); setSettleError(""); }}
                          className={`w-28 rounded-md px-3 py-1.5 text-sm ${settleError ? "border border-red-500" : "border border-white/50 dark:border-white/15 bg-white/80 dark:bg-white/5"}`}
                        />
                      </div>
                      {settlePanel.type === "TEMP_OUT" && (
                        <div className="flex-1 min-w-[240px]">
                          <label className="block text-xs font-medium text-muted mb-1">
                            Catatan (wajib) — kenapa barang ini tidak kembali
                          </label>
                          <input
                            type="text"
                            value={settleNote}
                            onChange={(e) => { setSettleNote(e.target.value); setSettleError(""); }}
                            placeholder="mis. dipakai buat acara X, habis terpakai"
                            className={`w-full rounded-md px-3 py-1.5 text-sm ${settleError ? "border border-red-500" : "border border-white/50 dark:border-white/15 bg-white/80 dark:bg-white/5"}`}
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        disabled={actingId === row.id}
                        onClick={confirmSettlePanel}
                        className={`rounded-md text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-60 ${
                          settlePanel.type === "RETURN" ? "bg-green-600 hover:bg-green-700" : "bg-amber-600 hover:bg-amber-700"
                        }`}
                      >
                        {actingId === row.id ? "…" : "Konfirmasi"}
                      </button>
                      <button
                        type="button"
                        onClick={closeSettlePanel}
                        className="rounded-md border border-white/50 dark:border-white/15 text-xs font-semibold px-3 py-1.5"
                      >
                        Batal
                      </button>
                      {settleError && <p className="w-full text-[11px] text-red-600">{settleError}</p>}
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
